import React, { useEffect, useMemo, useState } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Alert from "components/atoms/alert/Alert";
import Button, { BUTTON_STYLES } from "components/atoms/button";
import Checkbox from "components/atoms/input/Checkbox";
import FormInput from "components/atoms/input/FormInput";
import SelectBox from "components/atoms/input/SelectBox";
import useLoading from "components/atoms/loading/useLoading";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import AbstractModal from "components/page/modal/AbstractModal";

import { useSpaceAIMemberActivityType, useSpaceAIMemberCreate, useSpaceAIMemberUpdate } from "hooks/queries/useUser";

const Container = styled.div`
    width: 100%;
`;

const ContentStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 22px;
`;

const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const SectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
`;

const SectionTitle = styled(Text).attrs({
    variant: "body-md-500",
    color: "var(--color-Black)",
})``;

const SectionDescription = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})`
    margin-top: 3px;
`;

const ProfileGrid = styled.div`
    display: grid;
    grid-template-columns: 64px minmax(0, 1fr) 140px 92px;
    align-items: end;
    gap: 12px;
`;

const AvatarField = styled.div`
    width: 64px;
`;

const AvatarContainer = styled.div`
    position: relative;
    width: 56px;
    height: 56px;
`;

const AvatarWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    overflow: hidden;
    border: 1px solid var(--color-Line);
    border-radius: 50%;
    background: var(--color-Background);
    color: var(--color-SubBlack);
    font-size: 2rem;
    font-weight: 500;
`;

const AvatarImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const CameraBadge = styled.label`
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: 1px solid var(--color-Line);
    border-radius: 50%;
    background: var(--color-White);
    cursor: pointer;
`;

const HiddenFile = styled.input`
    display: none;
`;

const FieldLabel = styled(Text).attrs({
    variant: "body-sm-500",
    color: "var(--color-SubBlack)",
})`
    margin-bottom: 4px;
`;

const Textarea = styled.textarea`
    box-sizing: border-box;
    width: 100%;
    min-height: 88px;
    padding: 10px 12px;
    border: 1px solid var(--color-InputLine);
    border-radius: 4px;
    resize: vertical;
    font-size: 13px;
    line-height: 1.5;
    color: var(--color-SubBlack);
    background: var(--color-White);

    &:focus {
        outline: none;
        border-color: var(--color-Key);
    }

    &::placeholder {
        color: var(--color-Grey2);
    }
`;

const SelectWrap = styled.div`
    flex: 1;
`;

const ToggleControl = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 0 8px;
    border: 1px solid transparent;
    border-radius: 4px;
    background: ${(props) => (props.$active ? "var(--color-Base1)" : "transparent")};
`;

const Divider = styled.div`
    width: 100%;
    height: 1px;
    background: var(--color-Outline);
`;

const ActivityList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const ActivityCard = styled.div`
    border: 1px solid var(--color-Outline);
    border-radius: 6px;
    background: var(--color-White);
    overflow: hidden;
`;

const ActivityCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    min-height: 56px;
    padding: 12px 14px 12px 16px;
    background: var(--color-Base1);
`;

const ActivityTitleButton = styled.button`
    display: block;
    flex: 1;
    min-width: 0;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
`;

const ActivityTitleWrap = styled.div`
    min-width: 0;
    text-align: left;
`;

const ActivityTitle = styled(Text).attrs({
    variant: "body-md-500",
    color: "var(--color-Black)",
})`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ActivitySubtitle = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})`
    margin-top: 3px;
`;

const ActivityControls = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const CompactControl = styled.div`
    width: ${(props) => props.$width || "96px"};
`;

const ActivityCardBody = styled.div`
    display: ${(props) => (props.$expanded ? "flex" : "none")};
    flex-direction: column;
    padding: 14px 16px 16px;
`;

const LoadingText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-SubBlack)",
})`
    padding: 20px 0;
`;

const FooterButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    height: 32px;
`;

const RightButtons = styled.div`
    display: flex;
    align-items: center;
`;

const intervalItems = Array.from({ length: 24 }, (_, index) => {
    const value = index + 1;
    return {
        id: value,
        name: `${value}시간 마다`,
    };
});

const priorityItems = Array.from({ length: 10 }, (_, index) => {
    const value = index + 1;
    return {
        id: value,
        name: `${value} 순위`,
    };
});

const EMPTY_ACTIVITY_TYPES = [];

const createActivityState = (activityTypes = [], activitySettings = []) =>
    activityTypes.map((definition, index) => {
        const matchedSetting =
            activitySettings.find?.((activitySetting) => activitySetting.activity_type === definition.activity_type) ||
            activitySettings[index];

        return {
            ...definition,
            instruction: matchedSetting?.instruction || "",
            priority: matchedSetting?.priority || 1,
            is_enabled: matchedSetting?.is_enabled !== false,
            isExpanded: false,
        };
    });

function ActivitySettingCard({ activity, onChange }) {
    const updateActivity = (patch) => {
        onChange({
            ...activity,
            ...patch,
        });
    };

    return (
        <ActivityCard>
            <ActivityCardHeader>
                <ActivityTitleButton
                    type="button"
                    onClick={() => {
                        updateActivity({ isExpanded: !activity.isExpanded });
                    }}
                >
                    <ActivityTitleWrap>
                        <ActivityTitle>{activity.title}</ActivityTitle>
                        <ActivitySubtitle>{activity.description}</ActivitySubtitle>
                    </ActivityTitleWrap>
                </ActivityTitleButton>
                <ActivityControls>
                    <CompactControl $width="92px">
                        <SelectBox
                            items={priorityItems}
                            width={"100%"}
                            height={"32px"}
                            value={activity.priority}
                            onChange={(value) => updateActivity({ priority: value })}
                        />
                    </CompactControl>
                    <ToggleControl $active={activity.is_enabled}>
                        <Checkbox
                            label="활성"
                            checked={activity.is_enabled}
                            onChange={(e) => updateActivity({ is_enabled: e.target.checked })}
                        />
                    </ToggleControl>
                </ActivityControls>
            </ActivityCardHeader>
            <ActivityCardBody $expanded={activity.isExpanded}>
                <div>
                    <FieldLabel>활동 지시사항</FieldLabel>
                    <Textarea
                        placeholder="AI 멤버가 이 활동을 수행할 때 특별히 주의해야 할 사항 (선택입력)"
                        value={activity.instruction}
                        onChange={(e) => updateActivity({ instruction: e.target.value })}
                    />
                </div>
            </ActivityCardBody>
        </ActivityCard>
    );
}

function SpaceAIMemberEditModal({ spaceUuid, member, aiMembers = [], isLoading, exitModal }) {
    const { setLoading } = useLoading();
    const { setToast } = useToast();
    const activityTypeQuery = useSpaceAIMemberActivityType(spaceUuid);
    const activityTypes = activityTypeQuery.data || EMPTY_ACTIVITY_TYPES;

    const aiMemberSetting = useMemo(() => {
        if (!member?.id) {
            return null;
        }
        return aiMembers.find((aiMember) => aiMember.space_member_id === member.id) || null;
    }, [aiMembers, member]);

    const activitySettings = useMemo(() => aiMemberSetting?.activity_settings || [], [aiMemberSetting]);
    const isEditMode = Boolean(member);

    const [name, setName] = useState(member?.name || "");
    const [instruction, setInstruction] = useState("");
    const [intervalHours, setIntervalHours] = useState(24);
    const [isEnabled, setIsEnabled] = useState(true);
    const [activityFormList, setActivityFormList] = useState([]);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState("");

    const createMutation = useSpaceAIMemberCreate();
    const updateMutation = useSpaceAIMemberUpdate();
    const isSaveDisabled = name.trim().length < 1 || activityFormList.length < 1;

    useEffect(() => {
        if (!isEditMode) {
            setName("");
            setInstruction("");
            setIntervalHours(24);
            setIsEnabled(true);
            setActivityFormList(createActivityState(activityTypes));
            setImageFile(null);
            setImagePreviewUrl("");
            return;
        }

        setName(member?.name || "");

        if (!aiMemberSetting) {
            return;
        }

        setInstruction(aiMemberSetting.instruction || "");
        setIntervalHours(aiMemberSetting.interval_hours || 24);
        setIsEnabled(Boolean(aiMemberSetting.is_enabled));
        setActivityFormList(createActivityState(activityTypes, activitySettings));
        setImageFile(null);
        setImagePreviewUrl(aiMemberSetting.image || member?.image || "");
    }, [activitySettings, activityTypes, aiMemberSetting, isEditMode, member]);

    useEffect(() => {
        if (!imageFile) {
            return undefined;
        }

        const objectUrl = URL.createObjectURL(imageFile);
        setImagePreviewUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [imageFile]);

    const updateActivity = (index, nextActivity) => {
        setActivityFormList((prev) =>
            prev.map((activity, currentIndex) => (currentIndex === index ? nextActivity : activity)),
        );
    };

    const buildPayloadData = () => ({
        name: name.trim(),
        instruction,
        interval_hours: Number(intervalHours),
        is_enabled: isEnabled,
        activity_settings: activityFormList.map((activity) => ({
            activity_type: activity.activity_type,
            instruction: activity.instruction,
            priority: Number(activity.priority),
            is_enabled: activity.is_enabled,
        })),
    });

    const buildPayload = () => {
        const payloadData = buildPayloadData();
        if (!imageFile) {
            return payloadData;
        }

        const formData = new FormData();
        Object.entries(payloadData).forEach(([key, value]) => {
            if (key === "activity_settings") {
                formData.append(key, JSON.stringify(value));
            } else {
                formData.append(key, value);
            }
        });
        formData.append("image", imageFile);
        return formData;
    };

    const handleImageChange = (e) => {
        if (!e.target.files || e.target.files.length === 0) {
            return;
        }
        if (e.target.files.length > 1) {
            Alert("warn", "이미지 업로드", "이미지는 한 개만 업로드해주세요.");
            return;
        }

        const file = e.target.files[0];
        if (file.size > 2 * 1024 * 1024) {
            Alert("warn", "이미지 업로드", "이미지는 2MB 이하로 업로드해주세요.");
            return;
        }
        if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
            Alert("warn", "이미지 업로드", "이미지는 JPG 또는 PNG 형식으로 업로드해주세요.");
            return;
        }

        setImageFile(file);
        e.target.value = "";
    };

    const handleSave = async () => {
        const trimmedName = name.trim();
        if (trimmedName.length < 1) {
            Alert("warn", "이름", "AI 멤버 이름을 입력해주세요.");
            return;
        }

        if (trimmedName.length > 255) {
            Alert("warn", "이름", "이름은 255자 이하로 입력해주세요.");
            return;
        }

        if (activityFormList.length < 1) {
            Alert("warn", "활동 설정", "AI 멤버 활동 유형을 불러오지 못했습니다.");
            return;
        }

        if (isEditMode && !aiMemberSetting?.id) {
            Alert("warn", "에러", "AI 멤버 설정 정보를 찾을 수 없습니다.");
            return;
        }

        try {
            setLoading(true);
            if (isEditMode) {
                await updateMutation.mutateAsync({
                    space_uuid: spaceUuid,
                    ai_member_id: aiMemberSetting.id,
                    data: buildPayload(),
                });
                setToast("AI 멤버 설정이 변경되었습니다.", "info");
            } else {
                await createMutation.mutateAsync({
                    space_uuid: spaceUuid,
                    data: buildPayload(),
                });
                setToast("AI 멤버를 추가했습니다.", "info");
            }
            exitModal();
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.error || "서버에 오류가 발생하였습니다.");
        } finally {
            setLoading(false);
        }
    };

    const footer = (
        <FooterButtons>
            <RightButtons>
                <Button {...BUTTON_STYLES.modalSecondary} onClick={exitModal} buttonText={"취소"} />
                <Button
                    {...BUTTON_STYLES.modalPrimary}
                    onClick={handleSave}
                    margin={"0 0 0 4px"}
                    buttonText={isEditMode ? "저장" : "추가"}
                    disabled={isSaveDisabled}
                />
            </RightButtons>
        </FooterButtons>
    );

    if (isEditMode && isLoading) {
        return (
            <AbstractModal modalTitle="AI 멤버 설정" width={560} exitModal={exitModal}>
                <LoadingText>AI 멤버 설정을 불러오는 중입니다.</LoadingText>
            </AbstractModal>
        );
    }

    if (activityTypeQuery.isLoading || activityTypeQuery.isError || activityFormList.length < 1) {
        const activityTypeMessage = activityTypeQuery.isError
            ? "AI 멤버 활동 유형을 불러오지 못했습니다."
            : "AI 멤버 활동 유형을 불러오는 중입니다.";

        return (
            <AbstractModal modalTitle={isEditMode ? "AI 멤버 설정" : "AI 멤버 추가"} width={560} exitModal={exitModal}>
                <LoadingText>{activityTypeMessage}</LoadingText>
            </AbstractModal>
        );
    }

    return (
        <AbstractModal
            modalTitle={isEditMode ? "AI 멤버 설정" : "AI 멤버 추가"}
            width={640}
            exitModal={exitModal}
            footer={footer}
        >
            <Container>
                <ContentStack>
                    <Section>
                        <SectionHeader>
                            <div>
                                <SectionTitle>기본 정보</SectionTitle>
                                <SectionDescription>
                                    AI 멤버의 이름, 응답 성향, 실행 간격을 설정합니다.
                                </SectionDescription>
                            </div>
                        </SectionHeader>
                        <ProfileGrid>
                            <AvatarField>
                                <FieldLabel>프로필</FieldLabel>
                                <AvatarContainer>
                                    <AvatarWrap>
                                        {imagePreviewUrl ? (
                                            <AvatarImg src={imagePreviewUrl} alt="AI 멤버 프로필" />
                                        ) : (
                                            (name.trim() || "?").slice(0, 1)
                                        )}
                                    </AvatarWrap>
                                    <CameraBadge htmlFor="space-ai-member-profile-image-input" title="이미지 업로드">
                                        <Icon name="camera" size={"13"} />
                                    </CameraBadge>
                                    <HiddenFile
                                        id="space-ai-member-profile-image-input"
                                        type="file"
                                        accept={["image/png", "image/jpeg", "image/jpg"].join(",")}
                                        onChange={handleImageChange}
                                    />
                                </AvatarContainer>
                            </AvatarField>
                            <FormInput
                                label="이름"
                                requiredMark
                                placeholder="AI 멤버 이름"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                            <SelectWrap>
                                <FieldLabel>활동 간격</FieldLabel>
                                <SelectBox
                                    items={intervalItems}
                                    width={"100%"}
                                    height={"40px"}
                                    value={intervalHours}
                                    onChange={(value) => setIntervalHours(value)}
                                />
                            </SelectWrap>
                            <ToggleControl $active={isEnabled}>
                                <Checkbox
                                    label="활성"
                                    checked={isEnabled}
                                    onChange={(e) => setIsEnabled(e.target.checked)}
                                />
                            </ToggleControl>
                        </ProfileGrid>
                        <div>
                            <FieldLabel>기본 응답 성향</FieldLabel>
                            <Textarea
                                placeholder="AI 멤버가 응답할 때 따를 기본 지시사항 (선택입력)"
                                value={instruction}
                                onChange={(e) => setInstruction(e.target.value)}
                            />
                        </div>
                    </Section>
                    <Divider />
                    <Section>
                        <SectionHeader>
                            <div>
                                <SectionTitle>활동 설정</SectionTitle>
                                <SectionDescription>활동별 지시사항과 실행 우선순위를 관리합니다.</SectionDescription>
                            </div>
                        </SectionHeader>
                        <ActivityList>
                            {activityFormList.map((activity, index) => (
                                <ActivitySettingCard
                                    key={activity.activity_type}
                                    activity={activity}
                                    onChange={(nextActivity) => updateActivity(index, nextActivity)}
                                />
                            ))}
                        </ActivityList>
                    </Section>
                </ContentStack>
            </Container>
        </AbstractModal>
    );
}

export default SpaceAIMemberEditModal;
